import { inject } from '@angular/core';
import { isCountryUrlSegment } from '@app/common/locale/domain/entities/locale-url-segment';
import { CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router';
import { AngularI18nService } from 'common/data/i18n/services/angular-i18n.service';
import { Language } from '@app/common/locale/domain/entities/language.enum';
import { DEFAULT_LANG } from '../constants/locale.constants';
import {
    isLocalePrefixSegment,
    normalizeLocaleToken,
} from '../../route-config/domain/entities/seo-locale';

/**
 * Guards para el routing relacionados con locales
 */

/**
 * Guard that validates the locale detected in the URL.
 * Language switching is handled by RouteBootstrapService on NavigationEnd.
 *
 * @param _route - La ruta actual (no utilizada)
 * @param segments - Segmentos de la URL
 * @returns `true` if the locale is supported, `false` otherwise
 */
export const applyLocaleCanMatch: CanMatchFn = (_route, segments): boolean => {
    const firstSegment: string | undefined = segments[0]?.path;

    // Segmento de pais actual (`/ar`, `/pe`, y a futuro `/ar-en`).
    if (isCountryUrlSegment(firstSegment ?? '')) {
        return true;
    }

    const localeFromUrl: string = normalizeLocaleToken(firstSegment ?? DEFAULT_LANG);

    if (!isLocalePrefixSegment(localeFromUrl)) {
        return false;
    }

    const localeFamily: string = localeFromUrl.split('-')[0] ?? '';

    if (localeFamily !== 'es' && localeFamily !== 'en') {
        return false;
    }

    return localeFromUrl === localeFamily || localeFromUrl.startsWith(`${localeFamily}-`);
};

/**
 * Mapa de idioma a prefijo de locale para URLs.
 */
const LANG_TO_LOCALE_PREFIX: Readonly<Record<string, string>> = {
    en: '/en',
};

/**
 * Guard para rutas legacy de 'my' (sin prefijo de locale).
 * Si el idioma activo no es español (por ejemplo, el usuario navegó desde
 * /en/my/login y un link interno de ngx-auth apuntó a /my/signup sin
 * prefijo), redirige a la URL equivalente con el prefijo de locale correcto.
 *
 * @param _route - La ruta actual (no utilizada)
 * @param state - El estado del router con la URL destino
 * @returns `true` si el idioma es español, o un `UrlTree` con el prefijo de locale
 */
export const useSpanishForLegacyMy: CanActivateFn = (_route, state): boolean | UrlTree => {
    const i18nService: AngularI18nService = inject(AngularI18nService);
    const currentLang: Language = i18nService.getLang();
    const localePrefix: string | undefined = LANG_TO_LOCALE_PREFIX[currentLang];

    if (!localePrefix) {
        return true;
    }

    const router: Router = inject(Router);

    return router.parseUrl(localePrefix + state.url);
};
